import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SessionType } from '@shared';
import {
  selectSession,
  selectStripeSubscribeSessionState,
} from '../model/selectors';
import { postStripeSubscriptionCheckoutSessionUrlRequest } from '..';
import { AdvisorNotVerifiedContent } from '../components/AdvisorNotVerifiedContent';
import { AdvisorNotPayingContent } from '../components/AdvisorNotPayingContent';

interface StripeSubscribeSession {
  isLoading: boolean;
  success: boolean;
  url: string | null;
}

const SUBSCRIPTION_FEATURES = [
  {
    title: 'Smart Predictions',
    description: `Predict your client's likelihood, start date, progression, and cost of their long-term care.`,
  },
  {
    title: 'Strategy Optimization',
    description:
      'Test the ROI of policies, annuities, and self-funding strategies to find the right strategy',
  },
  {
    title: 'Industry-Leading Accuracy',
    description: '65% more accurate than industry standard methods.',
  },
  {
    title: 'Enterprise-Grade Security',
    description:
      'HIPAA compliant, with practices that align with SOC-2, and ISO 27001 standards',
  },
];

export function AdvisorAccountActionRequired() {
  const dispatch = useDispatch();
  const { sessionType, auth0Session, stripe } = useSelector(selectSession);
  const priceId = auth0Session?.advisorUser?.appMetadata?.price_id_latest;
  const { advisor } = useSelector(selectSession);
  const stripeSubscribeSession = useSelector(
    selectStripeSubscribeSessionState,
  ) as StripeSubscribeSession;
  const [showVideoModal, setShowVideoModal] = useState(false);
  const subscriptionStatus = stripe?.subscriptionStatus;

  const onUpgrade = useCallback(async () => {
    dispatch(
      postStripeSubscriptionCheckoutSessionUrlRequest({
        advisorId: advisor?.advisorId ?? '',
        priceId,
      }),
    );
  }, [advisor, dispatch]);

  useEffect(() => {
    const { isLoading, success, url } = stripeSubscribeSession;

    if (!isLoading && success && url) {
      window.location.href = url;
    }
  }, [stripeSubscribeSession]);

  const contentByState = {
    [SessionType.advisorUnverifiedAccount]: (
      <AdvisorNotVerifiedContent
        showVideoModal={showVideoModal}
        onVideoModalChange={setShowVideoModal}
      />
    ),
    [SessionType.advisorNoSubscription]: (
      <AdvisorNotPayingContent
        isLoading={stripeSubscribeSession.isLoading}
        onUpgrade={onUpgrade}
        showVideoModal={showVideoModal}
        onVideoModalChange={setShowVideoModal}
        features={SUBSCRIPTION_FEATURES}
        subscriptionStatus={subscriptionStatus}
        hasHadFreeTrial={stripe?.hasHadFreeTrial}
      />
    ),
  } as const;

  return (
    <div>
      <main className="my-auto flex h-[calc(100vh)] items-center justify-center bg-primary-50">
        <div className="relative flex h-full flex-1 flex-col items-center justify-center">
          {contentByState[sessionType as keyof typeof contentByState]}
        </div>
      </main>
    </div>
  );
}
